/**
 * In-page CTAs that would reload the page, or jump nowhere, scroll instead.
 *
 * Two defects this fixes on the Design Services / consultation page:
 *
 * 1. Five "BOOK NOW" buttons are set to `/pages/consultation` — the page they
 *    are already on — so clicking one reloads the page. The link value is not
 *    wrong: the same buttons are cloned onto the buying-guide pages, where
 *    `/pages/consultation` is a different page and navigating is correct. Only
 *    the self-referential case is a bug, so it is handled here rather than by
 *    editing merchant-configured links.
 *
 * 2. "HOW IT WORKS" points at `#how-it-works`, and nothing on the page has that
 *    id. Horizon renders sections as `shopify-section-template--<id>__<name>`,
 *    where the numeric part changes per page, so a plain anchor can never match.
 *    A hash is resolved against the section name as a fallback.
 *
 * Deliberately conservative: if no scroll target can be found the click is left
 * alone and the browser does whatever the href says. `ld-sticky-cta` keeps its
 * own copy of this behaviour — it is mobile-only and self-contained, and
 * touching that file would mean taking on its recorded scroll-listener leak.
 */
(function () {
  'use strict';

  /* Horizon section ids end `__<section name>`. A hash is authored in kebab
     case ("#how-it-works"), a section name in snake case ("how_it_works"). */
  function sectionByName(name) {
    if (!name) return null;
    var key = name.replace(/-/g, '_');
    return document.querySelector('[id$="__' + key + '"]');
  }

  /* The band a "BOOK NOW" is asking for: the booking intro, else the enquiry
     band, else the form itself. Its presence is also what tells us this page
     has somewhere to scroll to at all. */
  function bookingTarget() {
    return (
      sectionByName('book_intro') ||
      sectionByName('contact') ||
      sectionByName('enquiry') ||
      document.querySelector('.ld-enquiry-form')
    );
  }

  function scrollTo(target) {
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

    /* Move focus with the viewport, or a keyboard user stays where they were
       and the next Tab carries on from the button they just pressed. */
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  }

  function onClick(event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    var link = event.target.closest && event.target.closest('a[href]');
    if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

    /* A bare "#" is a JS hook, not a destination — the megamenu's top-level
       items use it for their dropdown triggers. It resolves to the current
       page, so without this it would be read as a self-link and every nav item
       on Contact and consultation would scroll to the form. */
    var raw = (link.getAttribute('href') || '').trim();
    if (raw === '' || raw === '#') return;

    var url;
    try {
      url = new URL(link.href, window.location.href);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin) return;

    var target = null;

    if (url.hash) {
      /* A hash that already resolves is the browser's job, not ours. */
      if (document.getElementById(url.hash.slice(1))) return;
      target = sectionByName(url.hash.slice(1));
    } else if (url.pathname === window.location.pathname) {
      /* A link to the page you are on. Only meaningful where there is a
         booking band to scroll to; anywhere else, let it navigate. */
      target = bookingTarget();
    }

    if (!target) return;
    event.preventDefault();
    scrollTo(target);
  }

  /* One delegated listener on `document`, which is never re-rendered, so it
     survives the section morphs the theme editor performs. Registered once, and
     released on pagehide so nothing is left attached for a bfcache restore to
     duplicate. */
  if (!document.documentElement.hasAttribute('data-ld-cta-scroll')) {
    document.documentElement.setAttribute('data-ld-cta-scroll', '');
    document.addEventListener('click', onClick);
    window.addEventListener(
      'pagehide',
      function () {
        document.removeEventListener('click', onClick);
        document.documentElement.removeAttribute('data-ld-cta-scroll');
      },
      { once: true }
    );
  }
})();
